import React from 'react';
import { AlertTriangle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/Button';
interface TermsOfServiceProps {
  onNavigate: (page: string) => void;
}
export function TermsOfService({
  onNavigate
}: TermsOfServiceProps) {
  return <div className="min-h-screen bg-white" data-id="element-973">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white sticky top-0 z-10" data-id="element-974">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-975">
          <button onClick={() => onNavigate('landing')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-976">
            <ArrowLeft className="w-4 h-4" data-id="element-977" />
            <span className="text-sm font-medium" data-id="element-978">Back to Home</span>
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-979">
        <div className="mb-8" data-id="element-980">
          <h1 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-981">
            Terms of Service
          </h1>
          <p className="text-gray-600" data-id="element-982">Last Updated: January 27, 2026</p>
        </div>

        {/* Critical Warning */}
        <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-8" data-id="element-983">
          <div className="flex items-start gap-3" data-id="element-984">
            <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" data-id="element-985" />
            <div data-id="element-986">
              <h2 className="text-lg font-bold text-red-900 mb-2" data-id="element-987">
                BETA VERSION - READ CAREFULLY
              </h2>
              <p className="text-red-800 text-sm leading-relaxed" data-id="element-988">
                This is a beta testing version of Zapiis. Data storage is NOT
                HIPAA compliant and NOT encrypted to medical or legal standards.
                Do not enter highly sensitive personal information. This service
                is provided "as is" for testing purposes only.
              </p>
            </div>
          </div>
        </div>

        <div className="prose prose-gray max-w-none" data-id="element-989">
          {/* Section 1 */}
          <section className="mb-10" data-id="element-990">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-991">
              1. Acceptance of Terms
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-992">
              By accessing or using Zapiis ("the Service"), you agree to be
              bound by these Terms of Service. If you do not agree to these
              terms, you may not use the Service.
            </p>
            <p className="text-gray-700 leading-relaxed" data-id="element-993">
              These terms apply to all users, including beta testers,
              individuals documenting incidents, and legal professionals who may
              review exported documentation.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-10" data-id="element-994">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-995">
              2. NOT LEGAL ADVICE - NO ATTORNEY-CLIENT RELATIONSHIP
            </h2>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-4" data-id="element-996">
              <p className="text-gray-900 font-bold mb-3" data-id="element-997">
                CRITICAL DISCLAIMER:
              </p>
              <ul className="space-y-2 text-gray-800" data-id="element-998">
                <li className="flex items-start gap-2" data-id="element-999">
                  <span className="font-bold" data-id="element-1000">•</span>
                  <span data-id="element-1001">
                    Zapiis is a <strong data-id="element-1002">documentation tool only</strong>. We are
                    NOT a law firm.
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-1003">
                  <span className="font-bold" data-id="element-1004">•</span>
                  <span data-id="element-1005">
                    We are NOT attorneys and do NOT provide legal advice, legal
                    representation, or legal services of any kind.
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-1006">
                  <span className="font-bold" data-id="element-1007">•</span>
                  <span data-id="element-1008">
                    Use of this Service does NOT create an attorney-client
                    relationship between you and Zapiis or any of its employees,
                    contractors, or affiliates.
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-1009">
                  <span className="font-bold" data-id="element-1010">•</span>
                  <span data-id="element-1011">
                    We do NOT provide recommendations on what legal actions you
                    should or should not take.
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-1012">
                  <span className="font-bold" data-id="element-1013">•</span>
                  <span data-id="element-1014">
                    Any information, suggestions, or AI-generated content
                    provided by the Service is for informational purposes only
                    and should NOT be construed as legal advice.
                  </span>
                </li>
              </ul>
            </div>
            <p className="text-gray-700 leading-relaxed" data-id="element-1015">
              You should consult with a licensed attorney in your jurisdiction
              for legal advice specific to your situation. Nothing in this
              Service should be used as a substitute for competent legal
              counsel.
            </p>
          </section>

          {/* Section 3 */}
          <section className="mb-10" data-id="element-1016">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1017">
              3. Beta Version Limitations
            </h2>
            <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1018">
              3.1 Not HIPAA Compliant
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-1019">
              <strong data-id="element-1020">
                The current beta version is NOT compliant with the Health
                Insurance Portability and Accountability Act (HIPAA).
              </strong>
              Data is stored in your browser's local storage without encryption
              to HIPAA standards. Do not enter:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4" data-id="element-1021">
              <li data-id="element-1022">Protected Health Information (PHI)</li>
              <li data-id="element-1023">Highly sensitive medical records</li>
              <li data-id="element-1024">Social Security Numbers</li>
              <li data-id="element-1025">Financial account information</li>
              <li data-id="element-1026">
                Any information you would not want exposed if your browser data
                were compromised
              </li>
            </ul>

            <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1027">
              3.2 Data Storage and Security
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-1028">
              All data is stored locally in your web browser using localStorage.
              This means:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4" data-id="element-1029">
              <li data-id="element-1030">Data is NOT encrypted at rest</li>
              <li data-id="element-1031">Data is NOT backed up to cloud servers</li>
              <li data-id="element-1032">
                Clearing your browser cache or cookies will permanently delete
                all your data
              </li>
              <li data-id="element-1033">We cannot recover your data if it is lost</li>
              <li data-id="element-1034">
                Data is only accessible on the specific browser and device where
                you entered it
              </li>
            </ul>

            <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1035">
              3.3 No Warranty
            </h3>
            <p className="text-gray-700 leading-relaxed" data-id="element-1036">
              The Service is provided "AS IS" and "AS AVAILABLE" without
              warranties of any kind, either express or implied. We do not
              warrant that the Service will be uninterrupted, secure, or
              error-free.
            </p>
          </section>

          {/* Section 4 */}
          <section className="mb-10" data-id="element-1037">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1038">
              4. User Responsibilities
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-1039">
              You are solely responsible for:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-1040">
              <li data-id="element-1041">
                The accuracy and completeness of all information you enter into
                the Service
              </li>
              <li data-id="element-1042">
                Maintaining backups of your data using the export functionality
              </li>
              <li data-id="element-1043">
                Determining whether the Service is appropriate for your specific
                needs
              </li>
              <li data-id="element-1044">
                Consulting with qualified legal counsel before taking any legal
                action
              </li>
              <li data-id="element-1045">
                Complying with all applicable laws and regulations in your use
                of the Service
              </li>
              <li data-id="element-1046">
                Protecting your beta access password and preventing unauthorized
                access
              </li>
            </ul>
          </section>

          {/* Section 5 */}
          <section className="mb-10" data-id="element-1047">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1048">
              5. AI-Generated Content
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-1049">
              The Service may use artificial intelligence to generate case
              summaries, find attorneys, or provide other features. You
              acknowledge and agree that:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-1050">
              <li data-id="element-1051">
                AI-generated content may contain errors, inaccuracies, or
                misleading information
              </li>
              <li data-id="element-1052">
                AI-generated attorney recommendations are not endorsements and
                should be independently verified
              </li>
              <li data-id="element-1053">
                You must review and verify all AI-generated content before
                relying on it
              </li>
              <li data-id="element-1054">
                AI-generated content does not constitute legal advice or
                professional recommendations
              </li>
            </ul>
          </section>

          {/* Section 6 */}
          <section className="mb-10" data-id="element-1055">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1056">
              6. No Guarantee of Admissibility
            </h2>
            <p className="text-gray-700 leading-relaxed" data-id="element-1057">
              We make no representations or warranties that documentation
              created using this Service will be admissible as evidence in any
              legal proceeding. Admissibility is determined by courts based on
              rules of evidence, which vary by jurisdiction. Consult with an
              attorney regarding evidence requirements for your specific case.
            </p>
          </section>

          {/* Section 7 */}
          <section className="mb-10" data-id="element-1058">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1059">
              7. Limitation of Liability
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-1060">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, ZAPIIS AND ITS AFFILIATES
              SHALL NOT BE LIABLE FOR:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-1061">
              <li data-id="element-1062">
                Any indirect, incidental, special, consequential, or punitive
                damages
              </li>
              <li data-id="element-1063">Loss of data, revenue, profits, or business opportunities</li>
              <li data-id="element-1064">
                Damages resulting from your use or inability to use the Service
              </li>
              <li data-id="element-1065">
                Damages resulting from reliance on any information provided by
                the Service
              </li>
              <li data-id="element-1066">
                Legal outcomes or consequences of actions taken based on
                documentation created with the Service
              </li>
            </ul>
          </section>

          {/* Section 8 */}
          <section className="mb-10" data-id="element-1067">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1068">
              8. Acceptable Use
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-1069">
              You agree NOT to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-1070">
              <li data-id="element-1071">
                Use the Service for any illegal purpose or in violation of any
                laws
              </li>
              <li data-id="element-1072">Enter false, misleading, or fraudulent information</li>
              <li data-id="element-1073">
                Attempt to gain unauthorized access to the Service or other
                users' data
              </li>
              <li data-id="element-1074">
                Reverse engineer, decompile, or disassemble any part of the
                Service
              </li>
              <li data-id="element-1075">Use the Service to harass, threaten, or harm others</li>
              <li data-id="element-1076">
                Share your beta access credentials with unauthorized parties
              </li>
            </ul>
          </section>

          {/* Section 9 */}
          <section className="mb-10" data-id="element-1077">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1078">
              9. Changes to Service and Terms
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-1079">
              We reserve the right to modify, suspend, or discontinue the
              Service at any time without notice. We may also update these Terms
              of Service at any time. Continued use of the Service after changes
              constitutes acceptance of the modified terms.
            </p>
            <p className="text-gray-700 leading-relaxed" data-id="element-1080">
              When the Service transitions from beta to full release,
              significant changes will be made including HIPAA compliance,
              encryption, and cloud storage. These changes may require
              acceptance of new terms.
            </p>
          </section>

          {/* Section 10 */}
          <section className="mb-10" data-id="element-1081">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1082">
              10. Termination
            </h2>
            <p className="text-gray-700 leading-relaxed" data-id="element-1083">
              We may terminate or suspend your access to the Service at any
              time, with or without cause, with or without notice. Upon
              termination, you must cease all use of the Service and may lose
              access to any data stored in the Service.
            </p>
          </section>

          {/* Section 11 */}
          <section className="mb-10" data-id="element-1084">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1085">
              11. Governing Law
            </h2>
            <p className="text-gray-700 leading-relaxed" data-id="element-1086">
              These Terms shall be governed by and construed in accordance with
              the laws of [Your State/Country], without regard to its conflict
              of law provisions. Any disputes shall be resolved in the courts
              located in [Your Jurisdiction].
            </p>
          </section>

          {/* Section 12 */}
          <section className="mb-10" data-id="element-1087">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1088">
              12. Contact Information
            </h2>
            <p className="text-gray-700 leading-relaxed" data-id="element-1089">
              For questions about these Terms of Service, please contact us at:
              [Your Contact Email]
            </p>
          </section>

          {/* Final Notice */}
          <div className="bg-gray-100 border-l-4 border-gray-500 p-6 mt-12" data-id="element-1090">
            <p className="text-gray-900 font-bold mb-2" data-id="element-1091">
              BY USING THIS SERVICE, YOU ACKNOWLEDGE THAT:
            </p>
            <ul className="space-y-2 text-gray-800 text-sm" data-id="element-1092">
              <li data-id="element-1093">• You have read and understood these Terms of Service</li>
              <li data-id="element-1094">
                • You understand this is a beta version with significant
                limitations
              </li>
              <li data-id="element-1095">• You understand data is not encrypted or HIPAA compliant</li>
              <li data-id="element-1096">
                • You understand we are not lawyers and do not provide legal
                advice
              </li>
              <li data-id="element-1097">
                • You will consult with a licensed attorney for legal guidance
              </li>
              <li data-id="element-1098">• You accept all risks associated with using this Service</li>
            </ul>
          </div>
        </div>
      </main>
    </div>;
}